/**
 * Credit Admin Dashboard — PHASE 2.
 *
 * Limited scope per spec:
 *  - List doctors only (mom hidden by backend)
 *  - View one doctor's account + last 20 ledger entries
 *  - Grant 1-5 credits with REQUIRED note
 *
 * Backend enforces role + amount/note rules; frontend is advisory.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout';
import { adminCreditsApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Coins, Loader2, Search, Stethoscope } from 'lucide-react';

export default function CreditAdminCreditsPage() {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminCreditsApi.listDoctors({
        limit: 50,
        ...(search ? { search } : {}),
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e?.message || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="credit-admin-page">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Coins className="h-6 w-6 text-emerald-600" />
            Doktor Kredi Yönetimi
          </h1>
          <p className="text-sm text-slate-500">
            Doktorlara 1-5 kredi arası ekleyin. Anne hesapları bu panelden yönetilemez.
          </p>
        </div>

        <Card>
          <CardContent className="py-4 flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Doktor ara (e-posta veya isim)</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  placeholder="örn. dr.smith"
                  data-testid="credit-admin-search-input"
                />
              </div>
            </div>
            <Button onClick={load} disabled={loading} data-testid="credit-admin-search-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ara'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Stethoscope className="h-4 w-4" /> Doktorlar ({total})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-posta / İsim</TableHead>
                  <TableHead>Mod</TableHead>
                  <TableHead className="text-right">Bakiye</TableHead>
                  <TableHead className="text-right">Free 4K</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => {
                  const acc = d.account;
                  return (
                    <TableRow key={d.id} data-testid={`ca-doctor-row-${d.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900 text-sm">{d.email}</div>
                        <div className="text-xs text-slate-500">{d.name}</div>
                      </TableCell>
                      <TableCell>
                        {acc?.credit_mode ? (
                          <Badge variant="outline" className="capitalize">
                            {acc.credit_mode}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">hesap yok</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {acc?.balance ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {acc
                          ? `${(acc.free_4k_generation_total || 0) - (acc.free_4k_generation_used ? 1 : 0)}/${acc.free_4k_generation_total || 0}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setActive(d)}
                          data-testid={`ca-doctor-detail-btn-${d.id}`}
                        >
                          Yönet
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!items.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-6">
                      Sonuç yok.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {active && (
          <DoctorGrantDialog
            doctor={active}
            onClose={() => setActive(null)}
            onChanged={load}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function DoctorGrantDialog({ doctor, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [amount, setAmount] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    adminCreditsApi
      .getDoctor(doctor.id)
      .then(setDetail)
      .catch((e) => toast.error(e?.message || 'Yüklenemedi'));
  }, [doctor.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submit = async () => {
    const amt = parseInt(amount, 10);
    if (!Number.isFinite(amt) || amt < 1 || amt > 5) {
      toast.error('Miktar 1-5 arası olmalı');
      return;
    }
    if (note.trim().length < 3) {
      toast.error('Not en az 3 karakter olmalı');
      return;
    }
    setBusy(true);
    try {
      await adminCreditsApi.grantSmall(doctor.id, amt, note.trim());
      toast.success(`${amt} kredi eklendi.`);
      setNote('');
      reload();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.detail?.message || e?.message || 'Grant başarısız');
    } finally {
      setBusy(false);
    }
  };

  const acc = detail?.account;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="ca-doctor-detail-dialog">
        <DialogHeader>
          <DialogTitle>{doctor.email}</DialogTitle>
          <DialogDescription>{doctor.name} · doktor</DialogDescription>
        </DialogHeader>

        {!detail ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-xs text-slate-500">Bakiye</div>
                <div className="text-xl font-semibold tabular-nums" data-testid="ca-detail-balance">
                  {acc?.balance ?? 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Mod</div>
                <Badge variant="outline" className="capitalize">
                  {acc?.credit_mode}
                </Badge>
              </div>
              <div>
                <div className="text-xs text-slate-500">Free 4K</div>
                <div className="text-xl font-semibold tabular-nums">
                  {(acc?.free_4k_generation_total || 0) - (acc?.free_4k_generation_used ? 1 : 0)}/
                  {acc?.free_4k_generation_total || 0}
                </div>
              </div>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Kredi Ekle (1–5, not zorunlu)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex flex-wrap gap-2 items-end">
                  <div>
                    <Label className="text-xs">Miktar</Label>
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-24"
                      data-testid="ca-grant-amount"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Not</Label>
                    <Input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="örn. Bayisi etkinlik kredisi"
                      data-testid="ca-grant-note"
                    />
                  </div>
                  <Button onClick={submit} disabled={busy} data-testid="ca-grant-submit-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kredi Ver'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Son 20 Hareket</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Tip</TableHead>
                      <TableHead>Özellik</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail.history || []).map((h) => (
                      <TableRow key={h.id} data-testid={`ca-history-row-${h.id}`}>
                        <TableCell className="text-xs text-slate-500">
                          {(h.created_at || '').split('T')[0]}
                        </TableCell>
                        <TableCell>{h.type}</TableCell>
                        <TableCell>{h.feature_key || '—'}</TableCell>
                        <TableCell
                          className={`text-right tabular-nums ${h.amount > 0 ? 'text-emerald-700' : h.amount < 0 ? 'text-red-700' : ''}`}
                        >
                          {h.amount > 0 ? '+' : ''}
                          {h.amount}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
